import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { withRouter } from 'react-router-dom';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { Page } from '../../components';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import ComingSoonSocialPage from './ComingSoonSocialPage';
import CustomLandingPage from './CustomLandingPage';
import { validProps } from '../PageBuilder/Field';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const landingPageTitle = 'Riticket | Fair Ticket Marketplace for Real Fans';
const landingPageDescription =
  'Buy and sell tickets with lower fees, price insights, secure payments, Crews, LinkUps, and ticket bidding on Riticket.';

// Helper to read persistent flag safely in browser
const getHasLoggedInBefore = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem('hasLoggedInBefore') === 'true') return true;
  } catch (e) {
    // ignore
  }
  try {
    const cookie = document.cookie || '';
    return /(?:^|; )hasLoggedInBefore=true(?:;|$)/.test(cookie);
  } catch (e) {
    return false;
  }
};

const getLandingPageMetaProps = pageAssetsData => {
  const originalPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const meta = originalPageData?.meta || {};
  const title = validProps(meta.pageTitle)?.content;
  const description = validProps(meta.pageDescription)?.content;
  const socialSharing = validProps(meta.socialSharing);

  return {
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(socialSharing ? { socialSharing } : {}),
  };
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, history, currentUser, location } = props;
  const originalPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const pageMetaProps = getLandingPageMetaProps(pageAssetsData);
  const landingSeoProps = {
    title: pageMetaProps.title || landingPageTitle,
    description: pageMetaProps.description || landingPageDescription,
    socialSharing: pageMetaProps.socialSharing || {
      title: pageMetaProps.title || landingPageTitle,
      description: pageMetaProps.description || landingPageDescription,
    },
  };

  const [hasLoggedInBefore, setHasLoggedInBefore] = useState(false);

  useEffect(() => {
    setHasLoggedInBefore(getHasLoggedInBefore());
  }, []);

  // If a user is authenticated in this browser, persist the flag so that
  // subsequent logged-out visits show the generic landing page instead of
  // the social features coming page.
  useEffect(() => {
    if (!currentUser) return;
    try {
      if (typeof window !== 'undefined') {
        window.localStorage && window.localStorage.setItem('hasLoggedInBefore', 'true');
        // Also set a cookie for redundancy across storage scenarios
        document.cookie = 'hasLoggedInBefore=true; path=/; max-age=31536000';
        // Reflect immediately in component state so the UI follows even
        // within the same render cycle if currentUser changes soon after
        setHasLoggedInBefore(true);
      }
    } catch (e) {
      // ignore storage/cookie errors
    }
  }, [currentUser]);

  // Handle search from HeroSearchField
  const handleHeroSearch = searchQuery => {
    // Navigate to EventListingsPage with search query in URL so the page can parse it
    const q = encodeURIComponent(searchQuery || '');
    history.push(`/events?keywords=${q}`);
  };

  // Check if user came from home button (allows logged-out users to see landing page)
  // Use router-provided location if available; avoid global 'location' for SSR safety
  const searchStr = (location && location.search) || '';
  const urlParams = new URLSearchParams(searchStr);
  const fromHome = urlParams.get('from') === 'home';

  // For authenticated users, show the custom landing page with personalized content
  if (currentUser) {
    return (
      <CustomLandingPage
        {...props}
        isAuthenticated={true}
        currentUser={currentUser}
        seoMetaProps={landingSeoProps}
      />
    );
  }

  // For non-authenticated users who came from home button, show generic landing page
  if (fromHome) {
    return <CustomLandingPage {...props} isAuthenticated={false} seoMetaProps={landingSeoProps} />;
  }

  // For non-authenticated users (default), show ComingSoonSocialPage
  const handleLoginClick = () => {
    history.push('/login');
  };

  const handleExploreClick = () => {
    history.push('/signup');
  };

  return (
    <Page {...landingSeoProps}>
      <ComingSoonSocialPage
        onLoginClick={handleLoginClick}
        showExplore={true}
        onExploreClick={handleExploreClick}
        currentUser={currentUser}
      />
    </Page>
  );

  // If we have page data, modify hero sections and reorder sections
  let modifiedPageData = originalPageData;
  if (originalPageData?.sections) {
    // First, modify hero sections to use search field
    const sectionsWithModifiedHero = originalPageData.sections.map(section => {
      if (section.sectionType === 'hero' && section.callToAction) {
        return {
          ...section,
          callToAction: {
            fieldType: 'heroSearch',
            content: section.callToAction.content || 'Search for events, artists, venues...',
          },
        };
      }
      return section;
    });

    // Find specific sections by content identification
    const heroSection = sectionsWithModifiedHero.find(section => section.sectionType === 'hero');

    const featuredListingsSection = sectionsWithModifiedHero.find(
      section =>
        section.sectionType === 'carousel' && section.listings && section.listings.length > 0
    );

    const howItWorksSection = sectionsWithModifiedHero.find(
      section =>
        (section.sectionType === 'columns' || section.sectionType === 'features') &&
        ((typeof section.title === 'string' && section.title.toLowerCase().includes('how')) ||
          (typeof section.heading === 'string' && section.heading.toLowerCase().includes('how')) ||
          (typeof section.title === 'string' && section.title.toLowerCase().includes('works')) ||
          (typeof section.heading === 'string' && section.heading.toLowerCase().includes('works')))
    );

    const whyChooseSection = sectionsWithModifiedHero.find(
      section =>
        (section.sectionType === 'columns' || section.sectionType === 'features') &&
        ((typeof section.title === 'string' && section.title.toLowerCase().includes('why')) ||
          (typeof section.heading === 'string' && section.heading.toLowerCase().includes('why')) ||
          (typeof section.title === 'string' && section.title.toLowerCase().includes('choose')) ||
          (typeof section.heading === 'string' && section.heading.toLowerCase().includes('choose')))
    );

    // Build sections in the correct order: Hero → Featured Listings → How It Works → Why Choose Riticket
    const reorderedSections = [];

    if (heroSection) {
      reorderedSections.push(heroSection);
    }

    if (featuredListingsSection) {
      reorderedSections.push(featuredListingsSection);
    }

    if (howItWorksSection) {
      reorderedSections.push(howItWorksSection);
    }

    if (whyChooseSection) {
      reorderedSections.push(whyChooseSection);
    }

    // Add any remaining sections that weren't identified
    const identifiedSections = [
      heroSection,
      featuredListingsSection,
      howItWorksSection,
      whyChooseSection,
    ].filter(Boolean);
    const remainingSections = sectionsWithModifiedHero.filter(
      section => !identifiedSections.includes(section)
    );

    if (remainingSections.length > 0) {
      reorderedSections.push(...remainingSections);
    }

    modifiedPageData = {
      ...originalPageData,
      sections: reorderedSections,
    };
  }

  return (
    <PageBuilder
      pageAssetsData={modifiedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      options={{
        onHeroSearch: handleHeroSearch,
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  history: object.isRequired,
  location: object,
  currentUser: object,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { currentUser } = state.user || {};
  return { pageAssetsData, inProgress, error, currentUser };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(withRouter, connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
